import { ApiError, HttpClient } from "@utils/httpClient";
import type { MappingPlugin, MappingServiceViewModel } from "@models/service";
import type {
  CreateJobRequest,
  CreateJobResponse,
  CreateJobResponseViewModel,
  JobFilesResponse,
  JobFileViewModel,
  JobListResponse,
  JobListViewModel,
  JobStatus,
  JobStatusResponse,
  JobViewModel,
  OriginalFileResponse,
  ItemRequestPreviewRequest,
  ItemRequestPreviewResponse,
  SendItemRequestRequest,
  SendItemRequestResponse,
  ResultExportResponse,
  ResultExportViewModel,
  InputTemplateResponse,
} from "@models/job";
import type {
  MappingResult,
  MappingResultResponseWire,
} from "@models/mappingResult";
import { buildMappingResult } from "@utils/builders/mappingResult";
import type {
  PatchRequestViewModel,
  PatchResultResponse,
  PatchResultViewModel,
  RevisionListResponse,
  RevisionListViewModel,
} from "@models/revision";
import {
  buildCreateJobResponseViewModel,
  buildJobListViewModel,
  buildResultExportViewModel,
  buildJobFileViewModel,
  buildJobViewModelFromStatus,
} from "@utils/builders/job";
import {
  buildPatchRequest,
  buildPatchResultViewModel,
  buildRevisionListViewModel,
} from "@utils/builders/revision";
import { buildMappingServices } from "@utils/builders/service";

const BASE_URL = import.meta.env.VITE_MAPPING_API_BASE_URL;

if (!BASE_URL) {
  // Surface mis-configuration loudly during build/dev rather than
  // silently issuing requests to `undefined/...`.
  console.warn(
    "VITE_MAPPING_API_BASE_URL is not set. The mapping dashboard " +
      "will fail to fetch services until it is configured in .env.",
  );
}

export class MappingApi {
  private httpClient: HttpClient;

  constructor(httpClient?: HttpClient) {
    this.httpClient = httpClient ?? new HttpClient(BASE_URL ?? "");
  }

  /**
   * Fetch the mapping plugins a job can be created for (one card each):
   * every `(service, plugin)` with an active plugin config.
   */
  async listServices(): Promise<MappingServiceViewModel[]> {
    const plugins = await this.httpClient.makeAuthenticatedRequest<MappingPlugin[]>(
      "/plugins",
      { method: "GET" },
    );
    return buildMappingServices(plugins ?? []);
  }

  /**
   * Create a job + receive a presigned PUT for the file upload.
   * The pipeline starts when the file is PUT to `uploadUrl`.
   */
  async createJob(
    request: CreateJobRequest,
  ): Promise<CreateJobResponseViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<CreateJobResponse>(
        "/jobs",
        {
          method: "POST",
          body: JSON.stringify(request),
        },
      );
    return buildCreateJobResponseViewModel(response);
  }

  /**
   * PUT the file bytes to the presigned upload URL returned by `createJob`.
   * Content-Type must match `uploadContentType` from the create response
   * (S3 rejects mismatches because the presign is signed against it).
   *
   * Uses XMLHttpRequest so callers can subscribe to upload progress via
   * `onProgress(percent)`. `fetch` cannot stream upload progress in most
   * browsers as of 2026, so the swap is intentional.
   */
  async uploadFileToS3(
    uploadUrl: string,
    file: File | Blob,
    contentType: string,
    onProgress?: (percent: number) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", contentType);

      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(Math.min(100, Math.max(0, percent)));
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          resolve();
        } else {
          // Stable, semantic code so the UI can render a friendly message
          // per failure class (expired presign, oversize, server fault).
          const code =
            xhr.status === 403
              ? "upload_expired"
              : xhr.status === 413
                ? "upload_too_large"
                : xhr.status >= 500
                  ? "upload_server_error"
                  : "upload_http_error";
          reject(
            new ApiError(
              `Upload failed: ${xhr.status} ${xhr.statusText}`,
              xhr.status,
              code,
            ),
          );
        }
      };
      xhr.onerror = () => {
        reject(new ApiError("Upload network error", 0, "upload_network"));
      };
      xhr.onabort = () => {
        reject(new ApiError("Upload aborted", 0, "upload_aborted"));
      };
      xhr.send(file);
    });
  }

  /**
   * Paginated history of this org's jobs (newest first). Optional
   * `service` / `pluginId` / `status` filters scope the page server-side,
   * plus `userId` (job owner) and a `startDate` / `endDate` (`YYYY-MM-DD`)
   * creation window. Backend caps `limit` at 200; default is 50.
   */
  async listJobs(
    options: {
      cursor?: string | null;
      limit?: number;
      service?: string;
      pluginId?: string;
      status?: JobStatus;
      userId?: string | null;
      startDate?: string | null;
      endDate?: string | null;
    } = {},
  ): Promise<JobListViewModel> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.service) params.set("service", options.service);
    if (options.pluginId) params.set("plugin_id", options.pluginId);
    if (options.status) params.set("status", options.status);
    if (options.userId) params.set("user_id", options.userId);
    if (options.startDate) params.set("start_date", options.startDate);
    if (options.endDate) params.set("end_date", options.endDate);
    const qs = params.toString();
    const path = `/jobs${qs ? `?${qs}` : ""}`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<JobListResponse>(path, {
        method: "GET",
      });
    return buildJobListViewModel(response);
  }

  /**
   * Permanently delete a job, its per-row results, and S3 artifacts.
   * Owner-only on the backend: a 403 means the caller did not create the
   * job, a 409 means it is still processing. Resolves on 204.
   */
  async deleteJob(jobId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest<void>(
      `/jobs/${encodeURIComponent(jobId)}`,
      { method: "DELETE" },
    );
  }

  async getJob(jobId: string): Promise<JobViewModel> {
    const [response, files] = await Promise.all([
      this.httpClient.makeAuthenticatedRequest<JobStatusResponse>(
        `/jobs/${encodeURIComponent(jobId)}`,
        { method: "GET" },
      ),
      this.getJobFiles(jobId),
    ]);
    return { ...buildJobViewModelFromStatus(response), files };
  }

  async getJobFiles(jobId: string): Promise<JobFileViewModel[]> {
    const response = await this.httpClient.makeAuthenticatedRequest<JobFilesResponse>(
      `/jobs/${encodeURIComponent(jobId)}/files`,
      { method: "GET" },
    );
    return response.files.map(buildJobFileViewModel);
  }

  /** Resolve a signed original document URL by FILE# index, never process item ID. */
  async getJobOriginalFile(jobId: string, fileIndex: number): Promise<OriginalFileResponse> {
    return this.httpClient.makeAuthenticatedRequest<OriginalFileResponse>(
      `/jobs/${encodeURIComponent(jobId)}/files/${fileIndex}/original`,
      { method: "GET" },
    );
  }

  /**
   * Paginated per-row results. Returns 409 if the job hasn't reached
   * `Running` yet — callers should treat that as "not ready, retry later".
   */
  async getJobResult(
    jobId: string,
    options: { cursor?: string | null; limit?: number; fileIndex?: number } = {},
  ): Promise<MappingResult> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.fileIndex !== undefined) params.set("file_index", String(options.fileIndex));
    const qs = params.toString();
    const path = `/jobs/${encodeURIComponent(jobId)}/result${qs ? `?${qs}` : ""}`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<MappingResultResponseWire>(
        path,
        { method: "GET" },
      );
    return buildMappingResult(response);
  }

  /**
   * The empty OE input workbook a reviewer fills in and uploads.
   *
   * Base64 in, Blob out. The bytes are not stored anywhere — the file is
   * identical for every caller — so there is no presigned URL to hand back the
   * way the result export does.
   */
  async getOeInputTemplate(): Promise<{ blob: Blob; filename: string }> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<InputTemplateResponse>(
        "/plugins/wd/input-template",
        { method: "GET" },
      );
    const bytes = Uint8Array.from(atob(response.content_base64), (c) =>
      c.charCodeAt(0),
    );
    return {
      blob: new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      filename: response.filename,
    };
  }

  /**
   * Build the .xlsx of all result rows server-side and return a presigned
   * download URL. `downloadUrl` is null when the job has no rows.
   */
  async exportJobResult(jobId: string, fileIndex?: number): Promise<ResultExportViewModel> {
    const path = `/jobs/${encodeURIComponent(jobId)}/result/export` +
      (fileIndex === undefined ? "" : `?file_index=${fileIndex}`);
    const response =
      await this.httpClient.makeAuthenticatedRequest<ResultExportResponse>(path, {
        method: "GET",
      });
    return buildResultExportViewModel(response);
  }

  /**
   * Build — but do NOT send — the mail asking whoever maintains the item_mp
   * catalogue to create the entries this job is missing. Returns the resolved
   * recipients plus an EDITABLE subject/body, and the same content structured
   * as `lines` so a table can be rendered instead of parsing the text.
   *
   * The preview step is the point: `suggested_item_mp_text` is what the select
   * LLM proposed, not a fact, so the reviewer rewords it — or drops rows —
   * before a colleague reads it. Pass the response's `row_indexes` back to
   * {@link sendItemRequest} so what is sent and what is counted agree.
   *
   * Rows a reviewer has already resolved (corrected onto a real candidate, or
   * rejected) are excluded server-side, so this is never a request to create
   * something the reviewer just found. Returns 409 before the job has written
   * any row, and 400 when `to` falls outside the deployment's allowed domains.
   */
  async previewItemRequest(
    jobId: string,
    body: ItemRequestPreviewRequest = {},
  ): Promise<ItemRequestPreviewResponse> {
    const path = `/jobs/${encodeURIComponent(jobId)}/item-requests/preview`;
    return this.httpClient.makeAuthenticatedRequest<ItemRequestPreviewResponse>(
      path,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  /**
   * Send the item request by email.
   *
   * `subject` / `body` are sent VERBATIM when supplied — that is the text the
   * reviewer edited in the preview; omit both and the server builds the same
   * message the preview returned.
   *
   * Errors worth distinguishing in the UI: 409 `no_item_request_rows` (nothing
   * to ask for), 400 `mail_recipient_not_allowed` (address outside the allowed
   * domains), 503 `mail_not_configured` (this environment has no verified SES
   * sender — the feature is off here, not the caller's mistake), and 502
   * `mail_send_failed` (the provider rejected it).
   */
  async sendItemRequest(
    jobId: string,
    body: SendItemRequestRequest,
  ): Promise<SendItemRequestResponse> {
    const path = `/jobs/${encodeURIComponent(jobId)}/item-requests`;
    return this.httpClient.makeAuthenticatedRequest<SendItemRequestResponse>(
      path,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  /**
   * Apply a reviewer edit to one result row. The server writes the new
   * state in place on `mapping-results` AND appends a revision to the
   * audit log, returning both so the client can render without a
   * follow-up GET.
   */
  async patchResult(
    jobId: string,
    rowIndex: number,
    payload: PatchRequestViewModel,
  ): Promise<PatchResultViewModel> {
    const path = `/jobs/${encodeURIComponent(jobId)}/results/${rowIndex}`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<PatchResultResponse>(path, {
        method: "PATCH",
        body: JSON.stringify(buildPatchRequest(payload)),
      });
    return buildPatchResultViewModel(response);
  }

  /**
   * Paginated history of edits applied to one result row, newest first.
   */
  async listRevisions(
    jobId: string,
    rowIndex: number,
    options: { cursor?: string | null; limit?: number } = {},
  ): Promise<RevisionListViewModel> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.cursor) params.set("cursor", options.cursor);
    const qs = params.toString();
    const path =
      `/jobs/${encodeURIComponent(jobId)}/results/${rowIndex}/revisions` +
      (qs ? `?${qs}` : "");
    const response =
      await this.httpClient.makeAuthenticatedRequest<RevisionListResponse>(
        path,
        {
          method: "GET",
        },
      );
    return buildRevisionListViewModel(response);
  }

  /**
   * Roll a result row back to a prior revision. The server copies the
   * named revision's payload over the current state and appends a new
   * revision flagged `source=restore` (so the restore itself is
   * auditable and undoable).
   */
  async restoreRevision(
    jobId: string,
    rowIndex: number,
    revisionId: string,
  ): Promise<PatchResultViewModel> {
    const path =
      `/jobs/${encodeURIComponent(jobId)}/results/${rowIndex}` +
      `/revisions/${encodeURIComponent(revisionId)}/restore`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<PatchResultResponse>(path, {
        method: "POST",
        body: "{}",
      });
    return buildPatchResultViewModel(response);
  }
}

export const mappingApi = new MappingApi();
